import {useCallback, useMemo, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {Carousel} from "@/components";
import {ImageWithLoadSpinner} from "@/components/image-with-load-spinner";
import {POPULAR_THEMES} from "@/constants/popularThemes";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import styles from "./ThemesCarousel.module.css";

const ThemesCarousel = () => {
  const allGames = useMiniGames(state => state.list);
  const setStoreSearch = useMiniGames(state => state.setSearch);
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const carouselControlsRef = useRef(null);

  const themes = useMemo(
    () =>
      POPULAR_THEMES.map(theme => {
        const count = allGames.filter(game => {
          if (theme.filterClass) return game.class?.toLowerCase() === theme.filterClass;
          if (theme.filterName) return game.name?.toLowerCase().includes(theme.filterName);
          return false;
        }).length;
        return {...theme, count};
      }).filter(t => t.count > 0),
    [allGames]
  );

  const handleThemeClick = useCallback(
    theme => {
      if (authStatus !== "authenticated") {
        handleShowAuthModal();
        return;
      }
      const searchTerm = theme.filterName || theme.filterClass || theme.label;
      setStoreSearch(searchTerm);
      navigate("/gameslist/all");
    },
    [authStatus, handleShowAuthModal, setStoreSearch, navigate]
  );

  const handleCarouselInit = useCallback(controls => {
    carouselControlsRef.current = controls;
  }, []);

  if (themes.length === 0) return null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.title}>🔥 Los Más Buscados</h3>
        <div className={styles.arrows}>
          <button
            type="button"
            className={styles.arrowBtn}
            onClick={() => carouselControlsRef.current?.scrollPrev()}
            aria-label="Anterior"
          >
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className={styles.arrowBtn}
            onClick={() => carouselControlsRef.current?.scrollNext()}
            aria-label="Siguiente"
          >
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        </div>
      </div>
      <Carousel
        speed={0.5}
        pauseOnHover
        onInit={handleCarouselInit}
      >
        {themes.map(theme => (
          <button
            key={theme.id}
            type="button"
            className={styles.card}
            onClick={() => handleThemeClick(theme)}
          >
            <div className={styles.cardImageWrap}>
              <ImageWithLoadSpinner
                src={`/images/themes/${theme.id}.jpg`}
                alt={theme.label}
                wrapperClassName={styles.cardImageHost}
                imgClassName={styles.cardImg}
                objectFit="cover"
              />
              <div className={styles.cardOverlay} />
            </div>
            <div className={styles.cardInfo}>
              <span className={styles.cardLabel}>{theme.label}</span>
              <span className={styles.cardCount}>{theme.count} juegos</span>
            </div>
          </button>
        ))}
      </Carousel>
    </div>
  );
};

export default ThemesCarousel;
